import { useSearchParams } from "react-router-dom";

import { capture, buildLibraryAnalyticsProps } from "../../analytics";
import styles from "./LensRequestStrip.module.css";

const SUPPORT_EMAIL = "grids@matchmovemachine.com";

// Copy for the standalone strip. The zero-results state renders the request as
// a bare link next to "Show all products" (linkOnly), so it needs no copy of
// its own: the empty state already names the failure once.
const COPY = {
  inline: {
    heading: "Can't find your camera or lens?",
    text: "Tell us which combination you need. We add rare assets weekly.",
  },
};

// URLSearchParams is form-encoded: it turns spaces into "+", which most mail
// clients do not decode inside a mailto body. Encode each field by hand.
const buildMailtoHref = (searchTerm) => {
  const subject = encodeURIComponent("Grid request");
  const bodyLines = ["Camera and lens I am looking for:", ""];
  if (searchTerm) {
    bodyLines.push(`(searched for: ${searchTerm})`);
  }
  const body = encodeURIComponent(bodyLines.join("\n"));
  return `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
};

const LensRequestStrip = ({
  variant = "inline",
  view,
  sort,
  resultCount,
  totalCount,
  // Renders the request link on its own, for hosts that supply their own
  // heading and text. `variant` still travels to analytics, so the funnel for
  // a given placement stays comparable across presentation changes.
  linkOnly = false,
  linkClassName,
  arrowClassName,
}) => {
  const [searchParams] = useSearchParams();
  const copy = COPY[variant] || COPY.inline;

  // Reading the filters here rather than taking them as a prop is what lets the
  // inline variant on the homepage carry real context: it sits outside
  // LibraryItems and has no other way to see the active filters.
  const analyticsProps = buildLibraryAnalyticsProps({
    searchParams,
    view,
    sort,
    resultCount,
    totalCount,
  });

  const handleClick = () => {
    capture("lens_request_clicked", { ...analyticsProps, variant });
  };

  const requestLink = (
    <a
      className={linkClassName || styles.link}
      href={buildMailtoHref(searchParams.get("search") || "")}
      onClick={handleClick}
    >
      Request a grid
      <span className={arrowClassName || styles.arrow} aria-hidden="true">→</span>
    </a>
  );

  if (linkOnly) {
    return requestLink;
  }

  return (
    <section className={styles.main}>
      <div className={styles.content}>
        <h2 className="h3-medium">{copy.heading}</h2>
        <p className="h4-medium">{copy.text}</p>
        {requestLink}
      </div>
    </section>
  );
};

export default LensRequestStrip;
